import invariant from "tiny-invariant"
import { type Address, zeroAddress } from "viem"
import type { SupportedChainId } from "../config"
import { Currency } from "./Currency"
import type { Token } from "./Token"
import { NATIVES, WNATIVE } from "./constants"
import { type SerializedNative, nativeSchema } from "./schema"
import type { TokenType } from "./types"

interface NativeArgs {
  chainId: number | string
  decimals: number
  symbol: string
  name: string
  logoURI?: string | undefined
}

/**
 * The `Native` class represents a native blockchain currency, such as ETH, within the application.
 * It extends the `Currency` class and provides specific attributes and functionality for native currencies.
 * Instances of this class are used to work with native blockchain currencies in a consistent manner.
 */
export class Native extends Currency {
  /**
   * Identifier for the native currency, in the format `${chainId}:NATIVE`.
   * @type {string}
   */
  readonly id: `${SupportedChainId}:NATIVE` | "NATIVE"

  /**
   * Flag to indicate that this currency is a native blockchain currency.
   * @type {true}
   */
  readonly isNative = true as const

  /**
   * Flag to indicate that this currency is not a token-based currency.
   * @type {false}
   */
  readonly isToken = false as const

  /**
   * The URI of the token logo image.
   * @type {string}
   */
  readonly logoURI?: string | undefined

  /**
   * Default address for native tokens.
   * @type {Address}
   */
  readonly address: Address = zeroAddress

  /**
   * Constructs a `Native` instance representing a native blockchain currency.
   * @param {object} native - The constructor arguments.
   * @param {number} native.chainId - The identifier for the blockchain.
   * @param {number} native.decimals - Number of decimal places the currency uses.
   * @param {string} native.symbol - Symbol of the currency, e.g., 'ETH'.
   * @param {string} native.name - Name of the currency, e.g., 'Ethereum'.
   * @param {string} native.logoURI - URI of the currency logo image.
   */
  protected constructor({ logoURI, ...native }: NativeArgs) {
    super(native)
    this.id = this.chainId ? `${this.chainId}:NATIVE` : "NATIVE"
    this.logoURI = logoURI
  }

  /**
   * Checks if the current native currency equals another currency.
   * @param other - The other currency type to compare with.
   * @returns {boolean} - True if both currencies are equal, false otherwise.
   */
  equals(other: TokenType): boolean {
    return other.isNative && other.chainId === this.chainId
  }

  /**
   * Returns the wrapped version of this native currency.
   * @returns {Token} - The wrapped representation of the currency.
   */
  get wrapped(): Token {
    const wNative = WNATIVE[this.chainId as keyof typeof WNATIVE]
    invariant(!!wNative, "WRAPPED")
    return wNative
  }

  /**
   * A cache to store instances of `Native` for different chain IDs.
   * @type {Record<number, Native>}
   * @private
   */
  private static cache: Record<number, Native> = {}

  /**
   * Retrieves or creates a `Native` instance for the given chain ID.
   * @param {number} chainId - The identifier for the blockchain.
   * @param {string} logoURI - URI for the native currency logo image.
   * @returns {Native} - A `Native` instance representing the native currency of the specified blockchain.
   */
  static onChain(
    chainId: SupportedChainId,
    logoURI?: string | undefined,
  ): Native {
    const cached = this.cache[chainId]

    if (typeof cached !== "undefined") {
      return cached
    }

    const nativeCurrency = NATIVES[chainId]

    invariant(!!nativeCurrency, `NATIVE_CURRENCY: ${chainId}`)

    const { decimals, name, symbol } = nativeCurrency

    const native = new Native({
      chainId,
      decimals,
      name,
      symbol,
      logoURI,
    })

    this.cache[chainId] = new Native({
      chainId,
      decimals,
      name,
      symbol,
      logoURI,
    })

    return native
  }

  /**
   * Serializes the `Native` instance into a format suitable for storage or transmission.
   * @returns {SerializedNative} - The serialized representation of the native currency.
   */
  serialize(): SerializedNative {
    return nativeSchema.parse({
      isNative: this.isNative,
      name: this.name,
      symbol: this.symbol,
      decimals: this.decimals,
      chainId: this.chainId,
      logoURI: this.logoURI,
    })
  }

  /**
   * Deserializes a serialized native currency representation into a `Native` instance.
   * @param {SerializedNative} native - The serialized native currency representation.
   * @returns {Native} - A `Native` instance representing the native currency.
   */
  static deserialize(native: SerializedNative): Native {
    invariant(native.chainId, "CHAIN_ID")
    return Native.onChain(native.chainId)
  }
}
